import { ref } from 'vue'

/**
 * Composable that centralises Sparrow JWT token management.
 *
 * Usage:
 *   const { token, fetchToken, handleTokenRefresh, handleAuthFailure } =
 *     useSparrowAuth({ countryIso3, capabilities })
 *
 * - `countryIso3`  – a Vue ref/computed (or getter fn) that returns the ISO-3 code
 * - `capabilities` – string[] of required Sparrow permission capabilities
 *
 * Call `await fetchToken()` in onMounted; pass `handleTokenRefresh` and
 * `handleAuthFailure` to any @ishare/kestrel-vue component.
 */
export function useSparrowAuth({ countryIso3, capabilities, onError }) {
  const token = ref(null)

  function resolveIso3() {
    if (typeof countryIso3 === 'function') return countryIso3()
    return countryIso3?.value ?? countryIso3
  }

  function csrfToken() {
    return document.querySelector('[name="csrf-token"]')?.content
  }

  async function fetchToken() {
    const response = await fetch('/integrations/sparrow/auth/request_token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken(),
      },
      body: JSON.stringify({
        country_iso3: resolveIso3(),
        capabilities,
      }),
    })
    const data = await response.json()
    if (!response.ok) throw new Error(data.error || 'Failed to obtain token')
    token.value = data.access_token
    return data.access_token
  }

  async function handleTokenRefresh(currentToken) {
    const response = await fetch('/integrations/sparrow/auth/refresh_token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken(),
      },
      body: JSON.stringify({
        expired_token: currentToken,
        country_iso3: resolveIso3(),
        capabilities,
      }),
    })
    const data = await response.json()
    if (!response.ok) throw new Error(data.error || 'Failed to refresh token')
    token.value = data.access_token
    return data.access_token
  }

  function handleAuthFailure() {
    if (onError) {
      onError()
    } else {
      console.error('[useSparrowAuth] Authentication failed. Please reload the page.')
    }
  }

  return { token, fetchToken, handleTokenRefresh, handleAuthFailure }
}
